/**
 *  @class
 *  @function OverlayPopup
 */
if (!customElements.get("overlay-popup")) {
  const overlayPopupQueue = [];
  let overlayPopupOpen = null;

  class OverlayPopup extends HTMLElement {
    constructor() {
      super();

      this.moved = false;
      this.relocating = false;
      this.openTimer = null;
      this.countdownTimer = null;
      this.teaser = null;
      this._signedUp = false;
      this.readyAt = Date.now();
      this.previouslyFocused = null;
      this.sectionId = this.dataset.sectionId;
      this.frequency = this.dataset.frequency || "7";
      this.trigger = this.dataset.trigger || "delay";
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButton = this.querySelector('[id^="ModalClose-"]');
      this.wrapper = null;
      this.boundOnKeyDown = this.onKeyDown.bind(this);
      this.boundOnOverlayClick = this.onOverlayClick.bind(this);
      this.boundOnSectionSelect = this.onSectionSelect.bind(this);
      this.boundOnSectionDeselect = this.onSectionDeselect.bind(this);
      this.boundOnSectionUnload = this.onSectionUnload.bind(this);
      this.boundOnMouseOut = this.onMouseOut.bind(this);
      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnSignupSubmit = this.onSignupSubmit.bind(this);
      this.boundOnTeaserClick = this.onTeaserClick.bind(this);

      if (this.closeButton) {
        this.closeButton.addEventListener("click", () => this.hide());
      }
      this.addEventListener("keydown", this.boundOnKeyDown);
      this.addEventListener("click", this.boundOnOverlayClick);

      const dismiss = this.querySelector("[data-overlay-popup-dismiss]");
      if (dismiss) {
        dismiss.addEventListener("click", () => this.hide());
      }

      const copyButton = this.querySelector("[data-overlay-popup-copy]");
      if (copyButton) {
        copyButton.addEventListener("click", () => this.copyCoupon());
      }

      const ajaxForm = this.querySelector(".signup-container[data-ajax] form");
      if (ajaxForm) {
        ajaxForm.addEventListener("submit", this.boundOnSignupSubmit);
      }

      if (this.isDesignMode) {
        document.addEventListener("shopify:section:select", this.boundOnSectionSelect);
        document.addEventListener("shopify:section:deselect", this.boundOnSectionDeselect);
        document.addEventListener("shopify:section:unload", this.boundOnSectionUnload);
        return;
      }

      this.bindTriggers();
    }

    connectedCallback() {
      if (this.moved) {
        return;
      }
      this.moved = true;
      this.relocating = true;
      document.body.appendChild(this);
      this.relocating = false;
    }

    disconnectedCallback() {
      if (this.relocating) {
        return;
      }
      this.unbindTriggers();
      this.stopCountdown();
      this.removeTeaser();
      this.removeFromQueue();
      if (overlayPopupOpen === this) {
        overlayPopupOpen = null;
      }
      document.removeEventListener("shopify:section:select", this.boundOnSectionSelect);
      document.removeEventListener("shopify:section:deselect", this.boundOnSectionDeselect);
      document.removeEventListener("shopify:section:unload", this.boundOnSectionUnload);
    }

    get isDesignMode() {
      return Boolean(window.Shopify && Shopify.designMode);
    }

    get alwaysShow() {
      return this.dataset.alwaysShow === "true";
    }

    get prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    get isFinePointer() {
      return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    }

    get isMobile() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    get storageKey() {
      const campaign = (this.dataset.campaign || "").trim();
      const base = `overlay-popup:${this.id}`;
      return campaign ? `${base}:${campaign}` : base;
    }

    get subscribedKey() {
      return `${this.storageKey}:subscribed`;
    }

    get endedKey() {
      return `${this.storageKey}:ended`;
    }

    get storage() {
      return this.frequency === "session" ? window.sessionStorage : window.localStorage;
    }

    shouldSkipDevice() {
      if (this.dataset.disableMobile === "true" && this.isMobile) {
        return true;
      }
      if (this.dataset.disableDesktop === "true" && !this.isMobile) {
        return true;
      }
      return false;
    }

    shouldSkipVisitor() {
      const visitor = this.dataset.visitor || "all";
      if (visitor === "all") {
        return false;
      }
      const seen = this.hasSeenPopup();
      if (visitor === "new") {
        return seen;
      }
      if (visitor === "returning") {
        return !seen;
      }
      return false;
    }

    hasSeenPopup() {
      return Boolean(this.readStorage()) || this.isSubscribed();
    }

    bindTriggers() {
      if (this.shouldSkipDevice()) {
        return;
      }
      if (!this.alwaysShow) {
        if (this.isDismissed() || this.isSubscribed() || this.wasEndedThisSession() || this.shouldSkipVisitor()) {
          return;
        }
      }

      if (this.trigger === "delay") {
        this.scheduleShow();
        return;
      }

      if (this.trigger === "exit_intent") {
        if (this.isFinePointer) {
          this.bindExitIntent();
        } else {
          this.scheduleShow();
        }
        return;
      }

      if (this.trigger === "scroll") {
        this.bindScroll();
        return;
      }

      if (this.trigger === "delay_or_exit") {
        this.scheduleShow();
        if (this.isFinePointer) {
          this.bindExitIntent();
        } else {
          this.bindScroll();
        }
      }
    }

    bindExitIntent() {
      document.documentElement.addEventListener("mouseout", this.boundOnMouseOut);
    }

    bindScroll() {
      window.addEventListener("scroll", this.boundOnScroll, { passive: true });
      this.onScroll();
    }

    unbindTriggers() {
      this.clearTimer();
      document.documentElement.removeEventListener("mouseout", this.boundOnMouseOut);
      window.removeEventListener("scroll", this.boundOnScroll);
    }

    scheduleShow() {
      const delaySeconds = Number(this.dataset.delay);
      if (!Number.isFinite(delaySeconds) || delaySeconds < 0) {
        return;
      }

      this.clearTimer();
      this.openTimer = setTimeout(() => {
        this.showFromTrigger();
      }, delaySeconds * 1000);
    }

    onMouseOut(event) {
      if (event.clientY > 0) {
        return;
      }
      if (Date.now() - this.readyAt < 1000) {
        return;
      }
      this.showFromTrigger();
    }

    onScroll() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) {
        return;
      }
      const percent = Number(this.dataset.scrollPercent);
      const threshold = Number.isFinite(percent) ? percent : 50;
      if ((window.scrollY / scrollable) * 100 >= threshold) {
        this.showFromTrigger();
      }
    }

    showFromTrigger() {
      this.unbindTriggers();
      this.show();
    }

    show(options) {
      const force = Boolean(options && options.force);
      if (this.hasAttribute("open")) {
        return;
      }

      if (!force && overlayPopupOpen && overlayPopupOpen !== this) {
        if (!overlayPopupQueue.includes(this)) {
          overlayPopupQueue.push(this);
        }
        return;
      }

      if (force && overlayPopupOpen && overlayPopupOpen !== this) {
        overlayPopupOpen.hide({ persist: false, skipQueue: true, skipTeaser: true });
      }

      overlayPopupOpen = this;
      this.hideTeaser();
      this.setAttribute("open", "");
      document.body.classList.add("overflow-hidden");
      this.wrapper = document.getElementById("wrapper");
      if (this.wrapper) {
        this.wrapper.inert = true;
      }

      this.previouslyFocused = document.activeElement;
      const focusDelay = this.prefersReducedMotion ? 0 : 100;
      setTimeout(() => {
        if (this.dialog) {
          this.dialog.focus();
        }
      }, focusDelay);

      this.startCountdown();
      this.dispatchPopupEvent("overlay-popup:shown");
      this.trackClarity("overlay-popup-shown");
    }

    hide(options) {
      const persist = !options || options.persist !== false;
      const skipQueue = Boolean(options && options.skipQueue);
      const skipTeaser = Boolean(options && options.skipTeaser);
      const fromEnded = Boolean(options && options.fromEnded);
      if (!this.hasAttribute("open")) {
        return;
      }

      this.stopCountdown();
      this.removeAttribute("open");
      document.body.classList.remove("overflow-hidden");
      if (this.wrapper) {
        this.wrapper.inert = false;
        this.wrapper = null;
      }

      if (persist && !this.isDesignMode) {
        this.persistDismissal();
      }

      this.restoreFocus();
      this.dispatchPopupEvent("overlay-popup:dismissed");
      this.trackClarity("overlay-popup-dismissed");

      if (overlayPopupOpen === this) {
        overlayPopupOpen = null;
      }

      const canTeaser =
        this.dataset.showTeaser === "true" &&
        !skipTeaser &&
        !this.isDesignMode &&
        !this._signedUp &&
        !fromEnded;

      if (canTeaser) {
        this.showTeaser();
      }

      if (!skipQueue) {
        this.flushQueue();
      }
    }

    flushQueue() {
      const next = overlayPopupQueue.shift();
      if (next && next.isConnected) {
        next.show();
      }
    }

    removeFromQueue() {
      const index = overlayPopupQueue.indexOf(this);
      if (index !== -1) {
        overlayPopupQueue.splice(index, 1);
      }
    }

    restoreFocus() {
      const target = this.previouslyFocused;
      this.previouslyFocused = null;
      if (target && typeof target.focus === "function" && document.contains(target) && !this.contains(target)) {
        target.focus();
      }
    }

    onKeyDown(event) {
      if (!this.hasAttribute("open")) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        this.hide();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        if (this.dialog) {
          this.dialog.focus();
        }
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onOverlayClick(event) {
      if (event.target === this) {
        this.hide();
      }
    }

    onSectionSelect(event) {
      if (event.detail.sectionId === this.sectionId) {
        this.show({ force: true });
      }
    }

    onSectionDeselect(event) {
      if (event.detail.sectionId === this.sectionId) {
        this.hide({ persist: false, skipTeaser: true, skipQueue: true });
      }
    }

    onSectionUnload(event) {
      if (event.detail.sectionId !== this.sectionId) {
        return;
      }
      this.hide({ persist: false, skipTeaser: true, skipQueue: true });
      this.remove();
    }

    onTeaserClick() {
      this.hideTeaser();
      this.show({ force: true });
    }

    showTeaser() {
      if (this.teaser) {
        this.teaser.hidden = false;
        return;
      }

      const button = document.createElement("button");
      button.type = "button";
      button.className = "overlay-popup-teaser";
      if (this.classList.contains("product-popup-modal--bottom-left")) {
        button.classList.add("overlay-popup-teaser--left");
      }
      const label = this.dataset.teaserLabel || "";
      button.textContent = label;
      button.setAttribute("aria-label", label);
      button.addEventListener("click", this.boundOnTeaserClick);
      document.body.appendChild(button);
      this.teaser = button;
    }

    hideTeaser() {
      if (this.teaser) {
        this.teaser.hidden = true;
      }
    }

    removeTeaser() {
      if (this.teaser) {
        this.teaser.remove();
        this.teaser = null;
      }
    }

    startCountdown() {
      this.stopCountdown();
      this.countdownEl = this.querySelector(".overlay-countdown");
      if (!this.countdownEl) {
        return;
      }
      this.tickCountdown();
      this.countdownTimer = setInterval(() => this.tickCountdown(), 1000);
    }

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }

    tickCountdown() {
      if (!this.countdownEl) {
        return;
      }
      const end = Number(this.countdownEl.dataset.end) * 1000;
      if (!Number.isFinite(end)) {
        this.countdownEl.hidden = true;
        this.stopCountdown();
        return;
      }

      const distance = end - Date.now();
      const clock = this.countdownEl.querySelector(".overlay-countdown__clock");
      const ended = this.countdownEl.querySelector(".overlay-countdown__ended");
      const pad = (value) => (value < 10 ? "0" + value : String(value));

      if (distance <= 0) {
        this.stopCountdown();
        this.countdownEl.querySelectorAll("[data-countdown-unit]").forEach((el) => {
          el.textContent = "00";
        });
        if (ended) {
          if (clock) {
            clock.hidden = true;
          }
          ended.hidden = false;
        }
        if (this.countdownEl.dataset.hidePopup === "true") {
          this.markEnded();
          this.hide({ persist: false, skipTeaser: true, fromEnded: true });
        }
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      const map = { days, hours, minutes, seconds };
      this.countdownEl.querySelectorAll("[data-countdown-unit]").forEach((el) => {
        const unit = el.getAttribute("data-countdown-unit");
        if (unit && map[unit] != null) {
          el.textContent = pad(map[unit]);
        }
      });
    }

    onSignupSubmit(event) {
      event.preventDefault();
      const form = event.target;
      if (typeof form.reportValidity === "function" && !form.reportValidity()) {
        return;
      }

      const errorEl = form.querySelector(".form-notification.error");
      const errorText = form.querySelector("[data-overlay-popup-error-text]");
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
      }

      fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("signup");
          }
          this.onSignupSuccess();
        })
        .catch(() => {
          if (errorEl) {
            errorEl.hidden = false;
            if (errorText) {
              errorText.textContent = this.dataset.signupError || "";
            }
          }
        })
        .finally(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
          }
        });
    }

    onSignupSuccess() {
      this._signedUp = true;
      this.persistSubscribed();
      this.removeTeaser();
      this.dispatchPopupEvent("overlay-popup:signup");
      this.trackClarity("overlay-popup-signup");

      const offer = this.querySelector(".overlay-popup__offer");
      const success = this.querySelector(".overlay-popup__success");
      if (offer) {
        offer.hidden = true;
      }
      if (success) {
        success.hidden = false;
        const focusTarget = success.querySelector("[data-overlay-popup-copy]") || success;
        if (typeof focusTarget.focus === "function") {
          focusTarget.focus();
        }
      }
    }

    copyCoupon() {
      const codeEl = this.querySelector("[data-overlay-popup-code]");
      const copyButton = this.querySelector("[data-overlay-popup-copy]");
      if (!codeEl) {
        return;
      }
      const code = codeEl.textContent.trim();
      const copiedLabel = this.dataset.copiedLabel || "";
      const copyLabel = this.dataset.copyLabel || "";

      const onCopied = () => {
        if (copyButton) {
          const label = copyButton.querySelector("span") || copyButton;
          label.textContent = copiedLabel;
          setTimeout(() => {
            label.textContent = copyLabel;
          }, 2000);
        }
        this.dispatchPopupEvent("overlay-popup:coupon-copied");
        this.trackClarity("overlay-popup-coupon-copied");
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(onCopied).catch(() => this.fallbackCopy(code, onCopied));
      } else {
        this.fallbackCopy(code, onCopied);
      }
    }

    fallbackCopy(code, onCopied) {
      const input = document.createElement("textarea");
      input.value = code;
      input.setAttribute("readonly", "");
      input.style.position = "absolute";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      try {
        document.execCommand("copy");
        onCopied();
      } catch (error) {
        /* ignore */
      }
      input.remove();
    }

    getFocusableElements() {
      const selector = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(",");

      return Array.from(this.querySelectorAll(selector)).filter((element) => {
        if (element.closest("[hidden]")) {
          return false;
        }
        const style = window.getComputedStyle(element);
        return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
      });
    }

    isSubscribed() {
      try {
        return Boolean(window.localStorage.getItem(this.subscribedKey));
      } catch (error) {
        return false;
      }
    }

    persistSubscribed() {
      try {
        window.localStorage.setItem(this.subscribedKey, JSON.stringify({ subscribedAt: Date.now() }));
      } catch (error) {
        /* private mode / quota */
      }
    }

    wasEndedThisSession() {
      try {
        return Boolean(window.sessionStorage.getItem(this.endedKey));
      } catch (error) {
        return false;
      }
    }

    markEnded() {
      try {
        window.sessionStorage.setItem(this.endedKey, "1");
      } catch (error) {
        /* ignore */
      }
    }

    isDismissed() {
      const raw = this.readStorage();
      if (!raw) {
        return false;
      }

      let record;
      try {
        record = JSON.parse(raw);
      } catch (error) {
        return true;
      }

      if (!record || !record.dismissedAt) {
        return false;
      }

      if (this.frequency === "never" || this.frequency === "session") {
        return true;
      }

      const days = Number(this.frequency);
      if (!Number.isFinite(days) || days <= 0) {
        return false;
      }

      return Date.now() - record.dismissedAt < days * 24 * 60 * 60 * 1000;
    }

    persistDismissal() {
      const payload = JSON.stringify({ dismissedAt: Date.now() });
      try {
        this.storage.setItem(this.storageKey, payload);
      } catch (error) {
        /* private mode / quota — popup still closes */
      }
    }

    readStorage() {
      try {
        return this.storage.getItem(this.storageKey);
      } catch (error) {
        return null;
      }
    }

    clearTimer() {
      if (this.openTimer) {
        clearTimeout(this.openTimer);
        this.openTimer = null;
      }
    }

    trackClarity(eventName) {
      if (typeof window.clarity === "function") {
        window.clarity("event", eventName);
      }
    }

    dispatchPopupEvent(eventName) {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail: {
            id: this.id,
            sectionId: this.sectionId,
            type: this.dataset.popupType || "announcement",
          },
        })
      );
    }
  }

  customElements.define("overlay-popup", OverlayPopup);
}
